import { DateTime } from 'luxon'
import type { DateRange } from '@dashboard/services/date'

export type ReportingDateRange = DateRange & { timezone?: string }

/**
 * A range bound's calendar day (YYYY-MM-DD): in the range's timezone when it has one (the picker and dashboard drills
 * set midnights there), otherwise the browser's, where the report store's default range is built.
 */
export function rangeDay(date: Date, timezone?: string): string {
  if (timezone) {
    return DateTime.fromJSDate(date, { zone: timezone }).toFormat('yyyy-MM-dd')
  }
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function calendarDate(date: Date, timezone: string): Date {
  return DateTime.fromJSDate(date, { zone: timezone }).setZone('local', { keepLocalTime: true }).toJSDate()
}

/** The range's calendar days as browser-local dates, for labels: a range with a timezone holds midnights there. */
export function calendarRange(range: ReportingDateRange): DateRange {
  return range.timezone
    ? { start: calendarDate(range.start, range.timezone), end: calendarDate(range.end, range.timezone) }
    : { start: range.start, end: range.end }
}

export function selectedDateRange(start: Date, end: Date, timezone: string): ReportingDateRange | null {
  const last = DateTime.fromJSDate(end).setZone(timezone, { keepLocalTime: true }).endOf('day')
  let first = DateTime.fromJSDate(start).setZone(timezone, { keepLocalTime: true }).startOf('day')
  if (!first.isValid || !last.isValid || first > last) {
    return null
  }
  const earliest = last.startOf('day').minus({ years: 1 }).plus({ days: 1 })
  if (first < earliest) {
    first = earliest
  }
  return { start: first.toJSDate(), end: last.toJSDate(), timezone }
}

export function dateRangeFromQuery(query: Record<string, unknown>): ReportingDateRange | null {
  const { from, to, timezone } = query
  if (typeof from !== 'string' || typeof to !== 'string' || typeof timezone !== 'string') {
    return null
  }
  const start = DateTime.fromISO(from, { zone: timezone }).startOf('day')
  const end = DateTime.fromISO(to, { zone: timezone }).endOf('day')
  if (!start.isValid || !end.isValid || end < start || start.toISODate() !== from || end.toISODate() !== to) {
    return null
  }
  return { start: start.toJSDate(), end: end.toJSDate(), timezone }
}
